import type { ApplyRowResult } from "../../../../api/roleProvisioning.service.js";
import { downloadCsv, escapeCsvValue, sanitizeUserInput } from "../../../../utils/csvExport.js";
import type { CsvRecord } from "./parseCsv.js";
import { type PreviewRow } from "./previewRows.js";

/**
 * The two files offered after a run (migration-plan.md §6.5).
 *
 * `skipped.csv` is the follow-up list for CS: every file row the run did not
 * process, with its ORIGINAL columns intact (so it can be re-submitted after a
 * fix — e.g. with a firm_ref added) plus `outcome` and `detail`. Original cells
 * are untrusted spreadsheet input, so they go through the formula-injection
 * guard; the outcome columns are ours.
 *
 * `applied.csv` is the undo list: one row per role actually inserted, with
 * `created` marking rows where the user account itself was created by this run
 * (undo for those is manual — the role delete alone does not remove the user).
 */

export const APPLIED_HEADERS = ["userId", "organisationId", "role", "created"] as const;

function line(cells: string[]): string {
  return cells.map(escapeCsvValue).join(",");
}

/**
 * @param headers The file's header cells, in file order.
 * @param records The parsed file rows, so each skipped row can carry its
 *   original cells — a row may have fewer cells than headers; missing ones
 *   are written empty.
 * @param skipped The preview rows in the skipped group.
 */
export function buildSkippedCsv(
  headers: string[],
  records: ReadonlyMap<number, CsvRecord>,
  skipped: PreviewRow[],
): string {
  const lines = [line([...headers.map(sanitizeUserInput), "outcome", "detail"])];
  for (const row of skipped) {
    const cells = records.get(row.lineNumber)?.cells ?? [];
    const original = headers.map((_header, index) => sanitizeUserInput(cells[index] ?? ""));
    lines.push(line([...original, row.outcome, row.detail]));
  }
  return `${lines.join("\n")}\n`;
}

/** Rows that inserted nothing (already_has_roles, not_a_member, error…) have nothing to undo. */
export function buildAppliedCsv(results: ApplyRowResult[]): string {
  const lines = [APPLIED_HEADERS.join(",")];
  for (const result of results) {
    for (const role of result.rolesInserted ?? []) {
      lines.push(
        line([
          result.userId ?? "",
          result.organisationId ?? "",
          role,
          result.status === "created" ? "true" : "false",
        ]),
      );
    }
  }
  return `${lines.join("\n")}\n`;
}

/** `2026-09-16-10-20-30` filename stamp, matching the backfill tools' files. */
function filenameStamp(now: Date): string {
  return now.toISOString().slice(0, 19).replace(/[:T]/g, "-");
}

export function skippedFilename(now: Date): string {
  return `role-provisioning-skipped-${filenameStamp(now)}.csv`;
}

export function appliedFilename(now: Date): string {
  return `role-provisioning-applied-${filenameStamp(now)}.csv`;
}

/**
 * @param at Stamps the filename — pass the moment of the download, so two
 *   downloads in one session cannot produce identically named files.
 */
export function downloadSkippedCsv(
  headers: string[],
  records: ReadonlyMap<number, CsvRecord>,
  skipped: PreviewRow[],
  at: Date,
): void {
  downloadCsv(buildSkippedCsv(headers, records, skipped), skippedFilename(at));
}

export function downloadAppliedCsv(results: ApplyRowResult[], at: Date): void {
  downloadCsv(buildAppliedCsv(results), appliedFilename(at));
}
